/**
 * Per-user module entitlements.
 *
 * tesseractConfig.ts decides which modules a DEPLOYMENT ships (VITE_MODULES).
 * This layer narrows that per signed-in user from `app_access.allowed_modules`
 * (migration 00521), loaded once by <AccessGate> after approval:
 *
 *   allowedModules = null      -> no restriction (every deployment module)
 *   allowedModules = ['a',...] -> only those modules; core modules (My Work,
 *                                 Configuration) are NOT implied
 *   app admin (GlobalAdmin / OrgAdmin, mirrors is_app_admin()) -> never restricted
 *
 * Visibility surfaces (home tiles, help panel, sidebar, command palette,
 * header shortcuts) call isModuleVisible / isContextVisible / isPathAllowed.
 * Deployment-level surfaces (Client Setup, route mounting) keep calling
 * isModuleEnabled, which stays user-agnostic on purpose.
 *
 * Routing: ROUTE_MODULES maps every top-level path in App.tsx to the module(s)
 * it belongs to. <ModuleRouteGuard> default-denies a restricted user on any
 * path not owned by an allowed module and sends them to their first allowed
 * module's home. A route test parses App.tsx so a new route without a mapping
 * fails CI instead of silently becoming unreachable for restricted users.
 */
import { useMemo } from 'react'
import { create } from 'zustand'
import type { ManagementContext } from '@/store/appStore'
import { MODULE_REGISTRY, isModuleEnabled, type ModuleKey } from './tesseractConfig'

/** Access roles that bypass entitlements. Mirrors public.is_app_admin(). */
export const ENTITLEMENT_ADMIN_ROLES = ['GlobalAdmin', 'OrgAdmin'] as const

export interface ModuleEntitlement {
  /** null = unrestricted. */
  allowedModules: ModuleKey[] | null
  isAdmin: boolean
}

export const UNRESTRICTED: ModuleEntitlement = { allowedModules: null, isAdmin: false }

const KNOWN_KEYS = new Set<string>(MODULE_REGISTRY.map((m) => m.key))

/**
 * Normalize a raw `allowed_modules` value from the database. Unknown keys are
 * dropped (a stale key must not widen access). A non-array (column missing on
 * a database that has not taken 00521 yet) reads as unrestricted.
 */
export function normalizeAllowedModules(raw: unknown): ModuleKey[] | null {
  if (!Array.isArray(raw)) return null
  return raw.filter((k): k is ModuleKey => typeof k === 'string' && KNOWN_KEYS.has(k))
}

export function isAdminAccessRole(role: string | null | undefined): boolean {
  return !!role && (ENTITLEMENT_ADMIN_ROLES as readonly string[]).includes(role)
}

/** Is this entitlement actually narrowing anything? */
export function isRestricted(e: ModuleEntitlement): boolean {
  return !e.isAdmin && e.allowedModules !== null
}

// ---------------------------------------------------------------------------
// Store (session-scoped, not persisted: re-read from app_access each sign-in)
// ---------------------------------------------------------------------------

interface EntitlementState extends ModuleEntitlement {
  /** Identity the landing redirect already ran for (once per sign-in). */
  landedFor: string | null
  setEntitlement: (e: ModuleEntitlement) => void
  markLanded: (identity: string) => void
}

export const useModuleEntitlementStore = create<EntitlementState>((set) => ({
  ...UNRESTRICTED,
  landedFor: null,
  setEntitlement: (e) => set({ allowedModules: e.allowedModules, isAdmin: e.isAdmin }),
  markLanded: (identity) => set({ landedFor: identity }),
}))

function current(): ModuleEntitlement {
  const s = useModuleEntitlementStore.getState()
  return { allowedModules: s.allowedModules, isAdmin: s.isAdmin }
}

/** Subscribe a component to entitlement changes; returns the live value. */
export function useModuleEntitlement(): ModuleEntitlement {
  const allowedModules = useModuleEntitlementStore((s) => s.allowedModules)
  const isAdmin = useModuleEntitlementStore((s) => s.isAdmin)
  return useMemo(() => ({ allowedModules, isAdmin }), [allowedModules, isAdmin])
}

// ---------------------------------------------------------------------------
// Visibility
// ---------------------------------------------------------------------------

export function isModuleVisibleFor(key: ModuleKey, e: ModuleEntitlement): boolean {
  if (!isModuleEnabled(key)) return false
  if (!isRestricted(e)) return true
  return e.allowedModules!.includes(key)
}

/** Deployment ships it AND the signed-in user is entitled to it. */
export function isModuleVisible(key: ModuleKey, e: ModuleEntitlement = current()): boolean {
  return isModuleVisibleFor(key, e)
}

const CONTEXT_TO_MODULE = new Map<ManagementContext, ModuleKey>()
for (const m of MODULE_REGISTRY) {
  for (const c of m.contexts) CONTEXT_TO_MODULE.set(c, m.key)
}

export function moduleForContext(ctx: ManagementContext): ModuleKey | undefined {
  return CONTEXT_TO_MODULE.get(ctx)
}

/**
 * User-aware isContextEnabled. A context with no owning module stays visible
 * for unrestricted users (as before) and hidden for restricted ones.
 */
export function isContextVisible(ctx: ManagementContext, e: ModuleEntitlement = current()): boolean {
  const key = CONTEXT_TO_MODULE.get(ctx)
  if (!key) return !isRestricted(e)
  return isModuleVisibleFor(key, e)
}

// ---------------------------------------------------------------------------
// Route ownership
// ---------------------------------------------------------------------------

/** Paths every approved user keeps, restricted or not. */
export const ALWAYS_ALLOWED_PATHS = ['/my-profile'] as const

/**
 * Top-level path prefix -> owning module(s). A path is open to a restricted
 * user when ANY listed module is allowed. Matched on whole segments, longest
 * prefix first. Keep in sync with AppRoutes in App.tsx (route test enforces).
 */
export const ROUTE_MODULES: Record<string, ModuleKey[]> = {
  // Portfolio & Project Management
  '/dashboard': ['ppm'],
  '/portfolios': ['ppm'],
  '/projects': ['ppm'],
  '/team': ['ppm'],
  '/attributes': ['ppm'],
  '/project-locations': ['ppm'],
  '/startup-checklist': ['ppm'],
  '/project-overview': ['ppm'],
  '/budget-releases': ['ppm'],
  '/resource-consumption': ['ppm'],
  '/project-requests': ['ppm'],
  '/priorities': ['ppm'],
  '/roi': ['ppm'],
  '/lessons-learned': ['ppm'],
  '/scenario-planning': ['ppm'],
  '/analysis': ['ppm'],
  '/risks': ['ppm'],
  '/milestone-analyzer': ['ppm'],
  '/programs': ['ppm'],
  '/gantt': ['ppm'],
  '/finance': ['ppm'],
  '/forecasts': ['ppm'],
  '/rates': ['ppm'],
  '/evm': ['ppm'],
  '/backlog': ['ppm'],
  '/board': ['ppm'],
  '/sprints': ['ppm'],
  '/roadmap': ['ppm'],
  '/agile-insights': ['ppm'],
  '/reports': ['ppm'],
  '/hierarchy': ['ppm'],
  '/implementation': ['ppm'],
  '/erp-sync': ['ppm'],
  '/project-fx': ['ppm'],
  '/estimate-import': ['ppm'],
  '/onboarding': ['ppm'],
  // My Work (personal command center + its cross-cutting surfaces)
  '/my-work': ['mywork'],
  '/notifications': ['mywork'],
  '/help': ['mywork'],
  '/skill-approvals': ['mywork', 'organizational'],
  // Workflow and Forms Management (legacy /workflows* redirect into it)
  '/workflow-management': ['workflow-management'],
  '/workflows': ['workflow-management'],
  '/workflows-legacy': ['workflow-management'],
  // Other studios
  '/contracts': ['clm'],
  '/timesheet': ['timesheet'],
  '/supply-chain': ['supplychain'],
  '/materials': ['supplychain'],
  '/personnel': ['personnel', 'organizational'],
  '/organizations': ['organizational'],
  '/cost': ['cost-management'],
  '/organization': ['cost-management'],
  '/crm': ['crm'],
  '/it-ticketing': ['it-ticketing'],
  '/analytics': ['analytics'],
  // Configuration
  '/config': ['configuration'],
  '/sap-integration': ['configuration'],
  '/developer': ['configuration'],
}

const PREFIXES = Object.keys(ROUTE_MODULES).sort((a, b) => b.length - a.length)

/**
 * Module(s) owning a pathname. Returns 'always' for always-allowed paths and
 * the home route, [] for an unmapped path (denied for restricted users).
 */
export function modulesForPath(pathname: string): ModuleKey[] | 'always' {
  const p = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  if (p === '' || p === '/') return 'always'
  for (const a of ALWAYS_ALLOWED_PATHS) {
    if (p === a || p.startsWith(`${a}/`)) return 'always'
  }
  for (const prefix of PREFIXES) {
    if (p === prefix || p.startsWith(`${prefix}/`)) return ROUTE_MODULES[prefix]!
  }
  return []
}

export function isPathAllowedFor(pathname: string, e: ModuleEntitlement): boolean {
  if (!isRestricted(e)) return true
  const owners = modulesForPath(pathname)
  if (owners === 'always') return true
  return owners.some((k) => isModuleVisibleFor(k, e))
}

/** Can the signed-in user open this path? Unrestricted users: always true. */
export function isPathAllowed(pathname: string, e: ModuleEntitlement = current()): boolean {
  return isPathAllowedFor(pathname, e)
}

// ---------------------------------------------------------------------------
// Landing
// ---------------------------------------------------------------------------

/** Where each module's home tile lands. */
export const MODULE_HOME_PATH: Record<ModuleKey, string> = {
  ppm: '/dashboard',
  clm: '/contracts',
  timesheet: '/timesheet',
  supplychain: '/supply-chain',
  personnel: '/personnel',
  organizational: '/organizations',
  mywork: '/my-work',
  'workflow-management': '/workflow-management/overview',
  'cost-management': '/cost',
  crm: '/crm',
  'it-ticketing': '/it-ticketing',
  configuration: '/config',
  analytics: '/analytics',
}

/** First allowed module in registry order, or null when none is reachable. */
export function firstAllowedModule(e: ModuleEntitlement): ModuleKey | null {
  const m = MODULE_REGISTRY.find((d) => isModuleVisibleFor(d.key, e))
  return m ? m.key : null
}

/** Landing path for a restricted user; My Profile when nothing is reachable. */
export function firstAllowedHome(e: ModuleEntitlement = current()): string {
  const key = firstAllowedModule(e)
  return key ? MODULE_HOME_PATH[key] : '/my-profile'
}

/**
 * Pure decision used by the guard (and its tests): where a restricted user on
 * `pathname` must be sent, or null to stay.
 */
export function resolveModuleRedirect(
  pathname: string,
  e: ModuleEntitlement,
  opts: { landing?: boolean } = {},
): string | null {
  if (!isRestricted(e)) return null
  const home = firstAllowedHome(e)
  const atRoot = pathname === '/' || pathname === ''
  if (atRoot) return opts.landing ? home : null
  return isPathAllowedFor(pathname, e) ? null : home
}
